/**
 * KontaktPage - Contact page
 *
 * SEO Target: /kontakt
 */

import { useState } from "react";
import { PageMeta, getBreadcrumbSchema, getLocalBusinessSchema, SEO_CONFIG } from "@/lib/seo";
import { Breadcrumbs } from "@/layouts/PublicLayout";
import { Helmet } from "react-helmet-async";
import { api } from "@/lib/api";
import { useCompanyInfo } from "@/hooks/use-company-info";

export function KontaktPage() {
  const { company, formattedOrgNumber, formattedCityOnly } = useCompanyInfo();
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const breadcrumbItems = [{ label: "Kontakt" }];

  const breadcrumbSchema = getBreadcrumbSchema([
    { name: "Hem", url: "/" },
    { name: "Kontakt", url: "/kontakt" },
  ]);

  const localBusinessSchema = getLocalBusinessSchema();

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    const formData = new FormData(e.currentTarget);
    const data = {
      name: formData.get("name") as string,
      email: formData.get("email") as string,
      subject: formData.get("subject") as string,
      message: formData.get("message") as string,
    };

    try {
      const response = await api.post("/contact", data);
      if (response.success) {
        setSubmitted(true);
      } else {
        setError(response.error?.message || "Ett fel uppstod. Försök igen senare.");
      }
    } catch {
      setError("Kunde inte skicka meddelandet. Kontrollera din internetanslutning och försök igen.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <PageMeta
        title="Kontakt - Kvanten Support"
        description="Kontakta Kvanten för frågor om årsredovisning, K2/K3 eller teknisk support. Vi svarar vanligtvis inom 24 timmar."
        keywords="kontakt Kvanten, support årsredovisning, K2 K3 hjälp"
        canonicalUrl={`${SEO_CONFIG.siteUrl}/kontakt`}
        fullTitle
      />

      <Helmet>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(localBusinessSchema)}</script>
      </Helmet>

      {/* Hero */}
      <section className="bg-gradient-to-b from-gray-50 to-white py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Breadcrumbs items={breadcrumbItems} />

          <div className="max-w-3xl">
            <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">Kontakta oss</h1>
            <p className="text-xl text-gray-600">
              Har du frågor om Kvanten, årsredovisning eller behöver teknisk support?
              Vi finns här för att hjälpa dig.
            </p>
          </div>
        </div>
      </section>

      {/* Contact options */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-8 mb-16">
            {[
              {
                title: "E-post",
                description: "För allmänna frågor och support",
                contact: company.email,
                href: `mailto:${company.email}`,
                icon: (
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                    />
                  </svg>
                ),
              },
              {
                title: "Vanliga frågor",
                description: "Hitta snabba svar på vanliga frågor",
                contact: "Se FAQ",
                href: "/vanliga-fragor",
                icon: (
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                ),
              },
              {
                title: "Svarstid",
                description: "Vi strävar efter att svara inom",
                contact: "24 timmar",
                icon: (
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                ),
              },
            ].map((item, i) => (
              <div key={i} className="bg-gray-50 rounded-xl p-6 text-center">
                <div className="w-12 h-12 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  {item.icon}
                </div>
                <h2 className="text-lg font-semibold text-gray-900 mb-2">{item.title}</h2>
                <p className="text-sm text-gray-500 mb-3">{item.description}</p>
                {item.href ? (
                  <a
                    href={item.href}
                    className="text-blue-600 font-medium hover:text-blue-700"
                  >
                    {item.contact}
                  </a>
                ) : (
                  <span className="text-gray-900 font-medium">{item.contact}</span>
                )}
              </div>
            ))}
          </div>

          {/* Contact form */}
          <div className="max-w-2xl mx-auto">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">
              Skicka ett meddelande
            </h2>

            {submitted ? (
              <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
                <svg
                  className="w-12 h-12 text-green-500 mx-auto mb-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Tack för ditt meddelande!</h3>
                <p className="text-gray-600">Vi återkommer till dig inom 24 timmar.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
                    {error}
                  </div>
                )}
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                      Namn *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                      E-post *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-1">
                    Ämne *
                  </label>
                  <select
                    id="subject"
                    name="subject"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Välj ämne...</option>
                    <option value="general">Allmän fråga</option>
                    <option value="support">Teknisk support</option>
                    <option value="billing">Faktura / Betalning</option>
                    <option value="k2k3">K2/K3-regelverk</option>
                    <option value="partnership">Samarbete / Byrå</option>
                    <option value="other">Övrigt</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
                    Meddelande *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows={5}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full px-6 py-3 text-lg font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? "Skickar..." : "Skicka meddelande"}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* Company info */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">Företagsinformation</h2>
          <div className="bg-white rounded-xl p-8 text-center">
            <p className="text-gray-600">
              <strong>{company.name}</strong>
              {formattedOrgNumber && (
                <>
                  <br />
                  Organisationsnummer: {formattedOrgNumber}
                </>
              )}
              <br />
              <br />
              E-post: <a href={`mailto:${company.email}`} className="text-blue-600">{company.email}</a>
              <br />
              <br />
              Säte: {formattedCityOnly}
            </p>
          </div>
        </div>
      </section>
    </>
  );
}
